import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Modal } from 'react-bootstrap';
import { updateBillDetails } from '../../services/BillService';
import { formatDate } from '../../utils/formatters';
import { getBankAccounts } from '../../services/BankAccountService';

const EditBillForm = ({ bill, onSuccess, onCancel, onDelete, simplified = true }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    amount: '',
    frequency: '',
    due_day: '',
    due_date: '',
    notes: '',
    bank_account: ''
  });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteAllFuture, setDeleteAllFuture] = useState(false);

  useEffect(() => {
    const fetchBankAccounts = async () => {
      try {
        const accounts = await getBankAccounts();
        setBankAccounts(accounts);
      } catch (err) {
        console.error('Error fetching bank accounts:', err);
      }
    };

    fetchBankAccounts();
  }, []);

  useEffect(() => {
    if (bill) {    
      // Try to get data from either the bill directly or from its requirement property
      const getBillValue = (field) => {
        // Try direct property first
        if (bill[field] !== undefined && bill[field] !== null) {
          return bill[field];
        }
        // Then try through requirement
        if (bill.requirement && bill.requirement[field] !== undefined && bill.requirement[field] !== null) {
          return bill.requirement[field];
        }
        return '';
      };
      
      // Format the due_date properly for the date input field (needs YYYY-MM-DD format)
      let formattedDueDate = '';
      if (bill.due_date) {
        try {
          // Try to parse the date and format it as YYYY-MM-DD
          const dueDate = new Date(bill.due_date);
          if (!isNaN(dueDate.getTime())) {
            const year = dueDate.getFullYear();
            const month = String(dueDate.getMonth() + 1).padStart(2, '0');
            const day = String(dueDate.getDate()).padStart(2, '0');
            formattedDueDate = `${year}-${month}-${day}`;
          } else {
            console.warn('Invalid due date');
          }
        } catch (err) {
          console.error('Error formatting due date:', err);
        }
      }
      
      // Get the bank account from either the bill or its requirement
      let bankAccountId = '';
      if (bill.bank_account) {
        bankAccountId = bill.bank_account.toString();
      } else if (bill.requirement && bill.requirement.bank_account) {
        bankAccountId = bill.requirement.bank_account.toString();
      }
      
      // Get frequency from multiple possible sources
      let frequency = '';
      if (bill.frequency) {
        frequency = bill.frequency;
      } else if (bill.requirement && bill.requirement.frequency) {
        frequency = bill.requirement.frequency;
      }
      
      setFormData({
        name: bill.name || '',
        description: bill.description || '',
        amount: bill.amount || '',
        frequency: frequency,
        due_day: getBillValue('due_day'),
        due_date: formattedDueDate,
        notes: bill.notes || '',
        bank_account: bankAccountId
      });
    }
  }, [bill]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.amount) {
      newErrors.amount = 'Amount is required';
    } else if (isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Amount must be a positive number';
    }
    
    // Only validate frequency if not in simplified mode
    if (!simplified && !formData.frequency) {
      newErrors.frequency = 'Frequency is required';
    }
    
    // Only validate due_day if not in simplified mode and frequency is monthly
    if (!simplified && formData.frequency === 'monthly' && !formData.due_day) {
      newErrors.due_day = 'Due day is required for monthly bills';
    } else if (!simplified && formData.due_day && (isNaN(formData.due_day) || formData.due_day < 1 || formData.due_day > 31)) {
      newErrors.due_day = 'Due day must be between 1 and 31';
    }
    
    if (!formData.due_date) {
      newErrors.due_date = 'Due date is required';
    }

    if (!formData.bank_account) {
      newErrors.bank_account = 'Bank account is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Make sure bank_account is a number, not a string
      const bankAccount = formData.bank_account ? parseInt(formData.bank_account, 10) : null;
      
      const updatedBill = {
        ...formData,
        amount: parseFloat(formData.amount),
        due_day: formData.due_day ? parseInt(formData.due_day) : null,
        bank_account: bankAccount
      };
      
      const response = await updateBillDetails(bill.id, updatedBill);
      onSuccess(response);
    } catch (err) {
      console.error('Error updating bill:', err);
      setError(`Failed to update bill: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    onDelete(deleteAllFuture);
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <Form onSubmit={handleSubmit}>
        {error && <Alert variant="danger">{error}</Alert>}
        
        <Form.Group className="mb-3">
          <Form.Label>Name</Form.Label>
          <Form.Control
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            isInvalid={!!errors.name}
          />
          <Form.Control.Feedback type="invalid">
            {errors.name}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            name="description"
            value={formData.description}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Amount</Form.Label>
          <Form.Control
            type="number"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            isInvalid={!!errors.amount}
            step="0.01"
          />
          <Form.Control.Feedback type="invalid">
            {errors.amount}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Only show frequency and due_day if not in simplified mode */}
        {!simplified && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Frequency</Form.Label>
              <Form.Select
                name="frequency"
                value={formData.frequency}
                onChange={handleChange}
                isInvalid={!!errors.frequency}
                disabled={bill && bill.requirement}
              >
                <option value="">Select frequency</option>
                <option value="weekly">Weekly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </Form.Select>
              {bill && bill.requirement && (
                <Form.Text className="text-muted">
                  This bill is part of a requirement series. To change the frequency, please edit the requirement.
                </Form.Text>
              )}
              <Form.Control.Feedback type="invalid">
                {errors.frequency}
              </Form.Control.Feedback>
            </Form.Group>

            {formData.frequency === 'monthly' && (
              <Form.Group className="mb-3">
                <Form.Label>Due Day</Form.Label>
                <Form.Control
                  type="number"
                  name="due_day"
                  value={formData.due_day}
                  onChange={handleChange}
                  isInvalid={!!errors.due_day}
                  min="1"
                  max="31"
                  disabled={bill && bill.requirement}
                />
                {bill && bill.requirement && (
                  <Form.Text className="text-muted">
                    This bill is part of a requirement series. To change the due day, please edit the requirement.
                  </Form.Text>
                )}
                <Form.Control.Feedback type="invalid">
                  {errors.due_day}
                </Form.Control.Feedback>
              </Form.Group>
            )}
          </>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Due Date</Form.Label>
          <Form.Control
            type="date"
            name="due_date"
            value={formData.due_date}
            onChange={handleChange}
            isInvalid={!!errors.due_date}
          />
          <Form.Control.Feedback type="invalid">
            {errors.due_date}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Notes</Form.Label>
          <Form.Control
            as="textarea"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Bank Account</Form.Label>
          <Form.Select
            name="bank_account"
            value={formData.bank_account}
            onChange={handleChange}
            isInvalid={!!errors.bank_account}
          >
            <option value="">Select bank account</option>
            {bankAccounts.map(account => (
              <option key={account.id} value={account.id}>
                {account.nickname || 'Unnamed'} - {account.bank || 'Unknown Bank'} ({account.acc_num || 'No Account Number'})
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            {errors.bank_account}
          </Form.Control.Feedback>
        </Form.Group>

        <div className="d-flex justify-content-between align-items-center">
          <Button
            variant="danger"
            type="button"
            onClick={handleDeleteClick}
          >
            Delete Bill
          </Button>
          
          <div>
            <Button
              variant="secondary"
              onClick={onCancel}
              className="me-2"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </Form>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this bill?</p>
          
          {bill && bill.bill_series_id && (
            <Form.Check
              type="checkbox"
              id="delete-all-future"
              label="Delete all future recurring bills in this series"
              checked={deleteAllFuture}
              onChange={(e) => setDeleteAllFuture(e.target.checked)}
              className="mt-3"
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditBillForm; 